import { DeliveryPricingBand } from './types'
import isEmpty from 'lodash/isEmpty'

interface ValidateDeliveryPricingBandsProps {
  pricingBands: DeliveryPricingBand[]
  index: number
}

interface ValidateDeliveryPricingBandProps {
  pricingBand: DeliveryPricingBand
}

interface IsWithInfiniteUpperThresholdProps {
  pricingBands: DeliveryPricingBand[]
}

interface FormatPricingBandsForPersistenceProps {
  pricingBands: DeliveryPricingBand[]
}

const ValidateDeliveryPricingBand = ({
  pricingBand
}: ValidateDeliveryPricingBandProps): [boolean, Record<string, boolean>] => {
  const { lowerThreshold, upperThreshold, percentageDiscount } =
    pricingBand || {}
  // check if lowerThreshold contains undefined
  const withInvalidLowerThreshold = lowerThreshold == null

  // check if percentageDiscount contains undefined
  const withInvalidPercentageDiscount = percentageDiscount == null

  // check if percentageDiscount is within acceptable range 1 - 100
  const withInvalidPercentageDiscountRange =
    percentageDiscount <= 0 || percentageDiscount > 100

  // check if lower range exceeds upper range
  const withInvalidLowerThresholdRange =
    parseInt(lowerThreshold?.toString()) >= parseInt(upperThreshold?.toString())

  // check if higher range is lower than the lower range
  const withInvalidUpperThresholdRange =
    parseInt(upperThreshold?.toString()) <= parseInt(lowerThreshold?.toString())

  return [
    withInvalidLowerThreshold ||
      withInvalidPercentageDiscount ||
      withInvalidPercentageDiscountRange ||
      withInvalidLowerThresholdRange ||
      withInvalidUpperThresholdRange,
    {
      withInvalidLowerThreshold,
      withInvalidPercentageDiscount,
      withInvalidPercentageDiscountRange,
      withInvalidLowerThresholdRange,
      withInvalidUpperThresholdRange
    }
  ]
}

const ValidateDeliveryPricingBands = ({
  pricingBands
}: Partial<ValidateDeliveryPricingBandsProps>) => {
  // check if lowerThreshold contains undefined
  const withInvalidLowerThreshold = !isEmpty(
    pricingBands?.find(
      ({ lowerThreshold }: DeliveryPricingBand) => lowerThreshold == null
    )
  )

  // check if percentageDiscount contains undefined
  const withInvalidPercentageDiscount = !isEmpty(
    pricingBands?.find(
      ({ percentageDiscount }: DeliveryPricingBand) =>
        percentageDiscount == null
    )
  )
  // check if percentageDiscount is within acceptable range 1 - 100
  const withInvalidPercentageDiscountRange = !isEmpty(
    pricingBands?.find(
      ({ percentageDiscount }: DeliveryPricingBand) =>
        percentageDiscount <= 0 || percentageDiscount > 100
    )
  )

  const withInvalidThresholdRanges = !isEmpty(
    pricingBands
      ?.filter(
        ({ upperThreshold }: DeliveryPricingBand) => upperThreshold != null
      )
      .find(
        ({ lowerThreshold, upperThreshold }: DeliveryPricingBand) =>
          parseInt(lowerThreshold?.toString()) >=
          parseInt(upperThreshold?.toString())
      )
  )

  return (
    withInvalidLowerThreshold ||
    withInvalidPercentageDiscount ||
    withInvalidPercentageDiscountRange ||
    withInvalidThresholdRanges
  )
}

const IsWithInfiniteUpperThreshold = ({
  pricingBands
}: IsWithInfiniteUpperThresholdProps) => {
  const lastPricingBandIndex = pricingBands.length - 1
  const { upperThreshold } = pricingBands[lastPricingBandIndex] || {
    upperThreshold: null
  }

  return !!pricingBands.length && upperThreshold == null
}

const FormatPricingBandsForPersistence = ({
  pricingBands
}: FormatPricingBandsForPersistenceProps) => {
  return pricingBands.map(
    ({
      lowerThreshold,
      upperThreshold,
      percentageDiscount
    }: DeliveryPricingBand) => {
      return {
        lowerThreshold: parseInt(lowerThreshold?.toString()),
        upperThreshold:
          upperThreshold != null ? parseInt(upperThreshold?.toString()) : null,
        percentageDiscount: parseInt(percentageDiscount.toString())
      }
    }
  )
}

export {
  ValidateDeliveryPricingBands,
  ValidateDeliveryPricingBand,
  IsWithInfiniteUpperThreshold,
  FormatPricingBandsForPersistence
}
